import {
  HttpClient,
  HttpEvent,
  HttpEventType,
  HttpResponse,
} from '@angular/common/http';
import { Observable, pipe } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { IApiConfig } from '../models/abstractions.model';

export abstract class LoganApiService {
  protected readonly module: string;
  protected readonly apiUrl: string;
  protected readonly apiKey: string;
  public isLocalHost: boolean = false;
  public loginAuth: boolean = false;
  public progress: number = 0;

  protected constructor(
    protected http: HttpClient,
    config: IApiConfig,
    module: string
  ) {
    this.module = module;
    this.apiUrl = config.url + '/api';
    this.apiKey = config.key;
    this.isLocalHost = this.apiUrl.search('localhost') > 0 ? true : false;
    if (this.module == 'auth') {
      this.loginAuth = true;
    }
  }

  protected getUrl(...params: Array<any>): string {
    let par =
      params !== null && params.length > 0 ? '/' + params.join('/') : '';
    return `${this.apiUrl}/${this.module}${par}`;
  }

  protected upload<TModel>(
    url: string,
    file: File,
    name: string = 'file',
    reportProgress: boolean = false
  ): Observable<TModel | null> {
    const formData: FormData = new FormData();
    formData.append(name, file, file.name);
    if (reportProgress)
      return this.http
        .post<TModel | null>(url, formData, {
          reportProgress: true,
          observe: 'events',
          withCredentials: true,
        })
        .pipe(
          uploadProgress((progress) => (this.progress = progress)),
          toResponseBody()
        );
    else return this.http.post<TModel>(url, formData);
  }

  protected uploadList<TModel>(
    url: string,
    files: Array<File>,
    name: string = 'file',
    reportProgress: boolean = false
  ): Observable<TModel | null> {
    const formData: FormData = new FormData();
    files.forEach((element) => {
      formData.append(name, element, element.name);
    });
    if (reportProgress)
      return this.http
        .post<TModel | null>(url, formData, {
          reportProgress: true,
          observe: 'events',
          withCredentials: true,
        })
        .pipe(
          uploadProgress((progress) => (this.progress = progress)),
          toResponseBody()
        );
    else return this.http.post<TModel>(url, formData);
  }

  protected uploadWithModel<TModel>(
    url: string,
    model: TModel,
    file: File,
    isPost: boolean = true,
    name: string = 'file',
    reportProgress: boolean = false
  ): Observable<TModel | null> {
    const formData: FormData = new FormData();

    formData.append('json', JSON.stringify(model));
    formData.append(name, file, file.name);

    if (reportProgress)
      if (isPost) {
        return this.http
          .post<TModel | null>(url, formData, {
            reportProgress: true,
            observe: 'events',
            withCredentials: true,
          })
          .pipe(
            uploadProgress((progress) => (this.progress = progress)),
            toResponseBody()
          );
      } else {
        return this.http
          .put<TModel | null>(url, formData, {
            reportProgress: true,
            observe: 'events',
            withCredentials: true,
          })
          .pipe(
            uploadProgress((progress) => (this.progress = progress)),
            toResponseBody()
          );
      }
    else if (isPost) {
      return this.http.post<TModel>(url, formData, { withCredentials: true });
    } else {
      return this.http.put<TModel>(url, formData, { withCredentials: true });
    }
  }
}

export function uploadProgress<T>(cb: (progress: number) => void): any {
  return tap((event: HttpEvent<T>) => {
    if (event.type === HttpEventType.UploadProgress) {
      cb(Math.round((100 * event.loaded) / (event.total ?? 100)));
    }
  });
}

export function toResponseBody<T>(): any {
  return pipe(
    filter((event: any): boolean => event.type === HttpEventType.Response),
    map((res: HttpResponse<T>) => res.body)
  );
}
